import type { UserId } from "./ids.js";

export type ScheduledGameAnnouncementId = `gameschedule_${string}`;

export const restartWarningTimeZone = "Europe/London";

export const systemRestartWarnings = [
  {
    id: "gameschedule_restart_15m",
    localMinute: 45,
    minutesBeforeRestart: 15,
    message: "[Restart] Automatic server restart in 15 minutes. Please finish what you are doing and prepare to safe-log."
  },
  {
    id: "gameschedule_restart_10m",
    localMinute: 50,
    minutesBeforeRestart: 10,
    message: "[Restart] Automatic server restart in 10 minutes. Please finish what you are doing and prepare to safe-log."
  },
  {
    id: "gameschedule_restart_5m",
    localMinute: 55,
    minutesBeforeRestart: 5,
    message: "[Restart] Automatic server restart in 5 minutes. Please safe-log now."
  },
  {
    id: "gameschedule_restart_2m",
    localMinute: 58,
    minutesBeforeRestart: 2,
    message: "[Restart] Automatic server restart in 2 minutes. Safe-log immediately."
  }
] as const satisfies ReadonlyArray<{
  id: ScheduledGameAnnouncementId;
  localMinute: number;
  minutesBeforeRestart: number;
  message: string;
}>;

export type SystemRestartWarning = (typeof systemRestartWarnings)[number];

const restartWarningById = new Map<string, SystemRestartWarning>(
  systemRestartWarnings.map((warning) => [warning.id, warning])
);

const restartWarningClock = new Intl.DateTimeFormat("en-GB", {
  timeZone: restartWarningTimeZone,
  hourCycle: "h23",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit"
});

export function systemRestartWarningForId(id: string): SystemRestartWarning | undefined {
  return restartWarningById.get(id);
}

export function isSystemRestartWarningRunSlot(id: string, candidate: Date): boolean {
  const warning = systemRestartWarningForId(id);
  if (!warning) return false;
  const parts = Object.fromEntries(
    restartWarningClock.formatToParts(candidate).map((part) => [part.type, part.value])
  );
  const hour = Number(parts.hour);
  return (hour === 0 || hour === 12)
    && Number(parts.minute) === warning.localMinute
    && Number(parts.second) === 0
    && candidate.getUTCMilliseconds() === 0;
}

/**
 * Returns the next exact 00:MM or 12:MM Europe/London warning after `after`.
 * Scanning UTC minutes keeps daylight-saving transitions explicit and avoids
 * inheriting the process or database host timezone.
 */
export function nextSystemRestartWarningRunAt(id: string, after: Date): Date | undefined {
  const warning = systemRestartWarningForId(id);
  if (!warning) return undefined;
  const firstMinute = Math.floor(after.getTime() / 60_000) * 60_000 + 60_000;
  for (let offsetMinutes = 0; offsetMinutes <= 25 * 60; offsetMinutes += 1) {
    const candidate = new Date(firstMinute + offsetMinutes * 60_000);
    const parts = Object.fromEntries(
      restartWarningClock.formatToParts(candidate).map((part) => [part.type, part.value])
    );
    const hour = Number(parts.hour);
    const minute = Number(parts.minute);
    if ((hour === 0 || hour === 12) && minute === warning.localMinute) return candidate;
  }
  throw new Error(`Unable to resolve the next ${restartWarningTimeZone} restart warning for ${id}.`);
}

export interface ScheduledGameAnnouncement {
  id: ScheduledGameAnnouncementId;
  serverId: string;
  message: string;
  intervalMinutes: number;
  enabled: boolean;
  nextRunAt: Date;
  lastRunAt?: Date;
  createdByUserId: UserId;
  updatedByUserId: UserId;
  createdAt: Date;
  updatedAt: Date;
}
